<template>
  <div 
    ref="scrollbar"
    class="scrollbar" 
    :style="cssVariables"
  >
    <div class="scrollbar__inner">
      <div class="scrollbar__btn scrollbar__btn_prev" @click="onPrevClick">
        <common-icon :icon-name="'IconScrollbar'" />
      </div>
      <div
          ref="refScrollbarTrack" 
          class="scrollbar__track"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
      >
        <div ref="refScrollbarThumb" class="scrollbar__thumb" />
      </div>
      <div class="scrollbar__btn scrollbar__btn_next" @click="onNextClick">
        <common-icon :icon-name="'IconScrollbar'" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
    import { type PropType, ref,  type Ref, computed } from "vue";
    import type { ConfigScrollbar, ExposeInterface } from "./types";
    import { type scrollGuideEvent, scrollTarget} from "../../types";

    const props = defineProps({
        config: {
            type: Object as PropType<ConfigScrollbar>,
            required: true
        }
    });
    const emit = defineEmits(['on-scroll-scrollbar'])

    const stepScroll = 10;
    const refScrollbarThumb: Ref<HTMLElement | null> = ref(null);
    const refScrollbarTrack: Ref<HTMLElement | null> = ref(null);
    const offsetSaved: Ref<number> = ref(0);

    const startScrollbarPosition = computed((): number | null => refScrollbarTrack.value ? refScrollbarTrack.value.offsetTop : null);
    const cssVariables = computed((): {[key: string]: string} => {
        if (!refScrollbarTrack.value || !refScrollbarThumb.value) {
            return {};
        }
        
        const heightTrack = refScrollbarTrack.value.clientHeight;
        const heightThumb = refScrollbarThumb.value.clientHeight;
        const offsetForTrack = props.config.scrollOffset * heightTrack / 100;
        const diff = (100 - props.config.scrollOffset) / 100;
        const offsetCSS = offsetForTrack - (heightThumb - (heightThumb * diff));
        const  config = {
            '--scrollbar-height': `${props.config.height}px`,
            '--scrollbar-offset': `${offsetCSS}px`,
            '--scrollbar-ratioHeightThumb': `${props.config.ratioHeightThumb}`,
        };
        return config;
    });


    const onTouchStart = (event: TouchEvent) => {
      setOffsetScrollbar(event.touches[0].pageY);
    };
    const setOffsetScrollbar = (currentTocuhPosition: number) => {
      if (!refScrollbarTrack.value || !startScrollbarPosition.value) {
        return;
      }

      const offset = currentTocuhPosition - startScrollbarPosition.value;
      const heightTrack = refScrollbarTrack.value.scrollHeight;
      let offsetInPercent: number = Number((offset  * 100 / heightTrack));
      const isStartcrollbar = offsetInPercent < 0;
      const isEndScrollbar = offsetInPercent >= 98;

      if (isStartcrollbar) {
        offsetInPercent = 0;
      }
      if (isEndScrollbar) {
        offsetInPercent = 100;
      }

      if (offsetSaved.value === offsetInPercent){
          return;
      }
      offsetSaved.value = offsetInPercent;
      
      const scrollEvent: scrollGuideEvent = {
          offset: offsetInPercent,
          ratioHeightThumb: props.config.ratioHeightThumb,
          target: scrollTarget.SCROLLBAR
      };

      emit('on-scroll-scrollbar', scrollEvent);
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!refScrollbarTrack.value) {
        return;
      }

      setOffsetScrollbar(event.touches[0].pageY);
    };
    const onPrevClick = () => {
      if (!refScrollbarTrack.value || !startScrollbarPosition.value) {
        return;
      }

      const stepInPercent = (100 - props.config.scrollOffset) / stepScroll;
      const offsetInPercent = props.config.scrollOffset - stepInPercent;
      const heightTrack = refScrollbarTrack.value.clientHeight;
      const currentTocuhPosition = Number(offsetInPercent * heightTrack / 100) + startScrollbarPosition.value; 

      setOffsetScrollbar(currentTocuhPosition);
    };
    const onNextClick = () => {
      if (!refScrollbarTrack.value || !startScrollbarPosition.value) {
        return;
      }

      const stepInPercent = (100 - props.config.scrollOffset) / stepScroll;
      const offsetInPercent = props.config.scrollOffset + stepInPercent;
      const heightTrack = refScrollbarTrack.value.clientHeight;
      const currentTocuhPosition = Number(offsetInPercent * heightTrack / 100) + startScrollbarPosition.value; 

      setOffsetScrollbar(currentTocuhPosition);
    };

    defineExpose<ExposeInterface>({
        setOffsetScrollbar,
        onPrevClick,
        onNextClick
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
